import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Music, Flame, Building2, Phone, Mail, MapPin, Lock, X, AlertTriangle } from "lucide-react";
import { AuthContext, API } from "../App";
import Footer from "../components/Footer";
import LanguageSelector from "../components/LanguageSelector";
import { ORG_ADDRESS, ORG_PHONE, ORG_PHONE_RAW, ORG_EMAIL_BOOKINGS } from "../constants";
import axios from "axios";

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [showNotificationDialog, setShowNotificationDialog] = useState(false);
  const [notificationMessages, setNotificationMessages] = useState([]);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const handleFacilityClick = async (facility, route) => {
    try {
      const res = await axios.get(`${API}/public/notifications/${facility}`);
      const notifications = res.data || [];
      if (notifications.length > 0) {
        setNotificationMessages(notifications);
        setPendingNavigation(route);
        setShowNotificationDialog(true);
        return;
      }
    } catch {
      // If fetch fails, just navigate directly
    }
    navigate(route);
  };

  const handleDismissNotification = () => {
    setShowNotificationDialog(false);
    setNotificationMessages([]);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Header */}
      <header className="bg-primary text-white border-b-4 border-black">
        <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
          <div className="flex justify-between items-start">
            <div className="flex-1 text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Building2 className="w-10 h-10 sm:w-12 sm:h-12" strokeWidth={2} />
              </div>
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-syne font-bold tracking-tight mb-2 notranslate" translate="no">
                YCK COURT RN PORTAL
              </h1>
              <p className="text-sm sm:text-lg text-white/90 max-w-xl mx-auto">
                <span className="notranslate" translate="no">Yio Chu Kang Court Residents&apos; Network</span><br/>
                Facility Booking System
              </p>
            </div>
            
            {/* Top Right - Language + Staff Login */}
            <div className="absolute top-4 right-4 sm:relative sm:top-0 sm:right-0 flex items-center gap-3">
              <LanguageSelector variant="dark" />
              {user ? (
                <button
                  onClick={() => user.is_admin ? navigate('/admin') : navigate('/kj-console')}
                  className="hidden sm:flex items-center gap-2 px-3 py-2 bg-white/20 hover:bg-white hover:text-primary text-white text-sm font-bold border-2 border-white/50 rounded transition-colors"
                  data-testid="staff-portal-btn"
                >
                  <Lock size={14} />
                  {user.is_admin ? 'ADMIN' : 'KJ CONSOLE'}
                </button>
              ) : (
                <button
                  onClick={() => navigate('/auth')}
                  className="hidden sm:flex items-center gap-2 px-3 py-2 bg-white/20 hover:bg-white hover:text-primary text-white text-sm font-bold border-2 border-white/50 rounded transition-colors"
                  data-testid="staff-login-btn"
                >
                  <Lock size={14} />
                  STAFF & KJ LOGIN
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Facility Selection */}
      <main className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        <h2 className="text-xl sm:text-2xl font-syne font-bold text-center mb-8">
          SELECT A FACILITY TO BOOK
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
          {/* Karaoke Booking Card */}
          <button
            onClick={() => handleFacilityClick('karaoke', '/karaoke')}
            className="group relative bg-white border-4 border-black p-6 sm:p-8 hover:shadow-brutal transition-all duration-200 hover:-translate-y-1 text-left"
            data-testid="karaoke-booking-card"
          >
            <div className="absolute top-0 right-0 bg-pink-500 text-white px-3 py-1 text-xs font-bold border-l-4 border-b-4 border-black notranslate" translate="no">
              KJ INCLUDED
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-accent border-4 border-black flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-white transition-colors">
                <Music className="w-10 h-10 sm:w-12 sm:h-12" strokeWidth={2} />
              </div>
              <h3 className="text-xl sm:text-2xl font-syne font-bold mb-2 notranslate" translate="no">KARAOKE BOOKING</h3>
              <p className="text-sm text-gray-600 mb-4">
                For Private Booking and <span className="notranslate" translate="no">Karaoke</span> Booking
              </p>
              <div className="space-y-1 text-sm">
                <p className="font-mono">🕐 1PM-5PM or 6PM-10PM</p>
                <p className="font-mono font-bold text-primary">💰 From $90/session</p>
              </div>
              <div className="mt-4 px-4 py-2 bg-primary text-white font-bold text-sm border-2 border-black group-hover:bg-black transition-colors">
                BOOK NOW →
              </div>
            </div>
          </button>

          {/* BBQ Pit Booking Card */}
          <button
            onClick={() => handleFacilityClick('bbq', '/bbq')}
            className="group relative bg-white border-4 border-black p-6 sm:p-8 hover:shadow-brutal transition-all duration-200 hover:-translate-y-1 text-left"
            data-testid="bbq-booking-card"
          >
            <div className="absolute top-0 right-0 bg-orange-500 text-white px-3 py-1 text-xs font-bold border-l-4 border-b-4 border-black notranslate" translate="no">
              OPEN
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-orange-100 border-4 border-black flex items-center justify-center mb-4 group-hover:bg-orange-500 group-hover:text-white transition-colors">
                <Flame className="w-10 h-10 sm:w-12 sm:h-12" strokeWidth={2} />
              </div>
              <h3 className="text-xl sm:text-2xl font-syne font-bold mb-2 notranslate" translate="no">BBQ PIT BOOKING</h3>
              <p className="text-sm text-gray-600 mb-4">
                Outdoor <span className="notranslate" translate="no">BBQ</span> facilities with 2 <span className="notranslate" translate="no">pits</span> available
              </p>
              <div className="space-y-1 text-sm">
                <p className="font-mono">🕐 1PM-5PM or 6PM-10PM</p>
                <p className="font-mono font-bold text-orange-600">💰 <span className="notranslate" translate="no">$20/pit</span>/session</p>
              </div>
              <div className="mt-4 px-4 py-2 bg-orange-500 text-white font-bold text-sm border-2 border-black group-hover:bg-black transition-colors">
                BOOK NOW →
              </div>
            </div>
          </button>
        </div>

        {/* Info Section */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
          <div className="p-4 bg-gray-50 border-2 border-black">
            <MapPin className="w-6 h-6 mx-auto mb-2 text-primary" />
            <p className="text-sm font-bold">Location</p>
            <p className="text-xs text-gray-600">{ORG_ADDRESS}</p>
          </div>
          <div className="p-4 bg-gray-50 border-2 border-black">
            <Phone className="w-6 h-6 mx-auto mb-2 text-primary" />
            <p className="text-sm font-bold">Contact</p>
            <p className="text-xs text-gray-600">
              <a href={`tel:${ORG_PHONE_RAW}`} className="text-primary hover:underline">{ORG_PHONE}</a>
            </p>
          </div>
          <div className="p-4 bg-gray-50 border-2 border-black">
            <Mail className="w-6 h-6 mx-auto mb-2 text-primary" />
            <p className="text-sm font-bold">Email</p>
            <p className="text-xs text-gray-600">
              <a href={`mailto:${ORG_EMAIL_BOOKINGS}`} className="text-primary hover:underline">{ORG_EMAIL_BOOKINGS}</a>
            </p>
          </div>
        </div>
        
        {/* Staff Login Link - Mobile (shown in footer area) */}
        <div className="sm:hidden mt-8 text-center">
          {user ? (
            <button
              onClick={() => user.is_admin ? navigate('/admin') : navigate('/kj-console')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 text-sm font-bold border-2 border-gray-300 rounded hover:bg-gray-200"
              data-testid="mobile-staff-portal-btn"
            >
              <Lock size={14} />
              {user.is_admin ? 'ADMIN PANEL' : 'KJ CONSOLE'}
            </button>
          ) : (
            <button
              onClick={() => navigate('/auth')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 text-sm font-bold border-2 border-gray-300 rounded hover:bg-gray-200"
              data-testid="mobile-staff-login-btn"
            >
              <Lock size={14} />
              Staff & KJ Login
            </button>
          )}
        </div>
      </main>

      {/* Notification Dialog */}
      {showNotificationDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="notification-dialog-overlay">
          <div className="bg-white border-4 border-black max-w-md w-full p-0 relative" data-testid="notification-dialog">
            {/* Header */}
            <div className="bg-amber-500 text-white px-5 py-3 flex items-center justify-between border-b-4 border-black">
              <div className="flex items-center gap-2">
                <AlertTriangle size={20} />
                <h3 className="font-bold text-lg">Notice</h3>
              </div>
              <button
                onClick={handleDismissNotification}
                className="p-1 hover:bg-white/20 rounded"
                data-testid="notification-dialog-close"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Messages */}
            <div className="px-5 py-4 space-y-3 max-h-[60vh] overflow-y-auto">
              {notificationMessages.map((n, i) => (
                <div key={n.notification_id || i} className="text-sm leading-relaxed text-gray-800 whitespace-pre-wrap">
                  {n.message}
                </div>
              ))}
            </div>
            
            {/* Footer */}
            <div className="px-5 py-3 border-t-2 border-gray-200 flex justify-end">
              <button
                onClick={handleDismissNotification}
                className="px-6 py-2 bg-primary text-white font-bold text-sm border-2 border-black hover:bg-primary/90 transition-colors"
                data-testid="notification-dialog-ok"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default LandingPage;
